import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Credit2000PaymentInfo } from 'ts/shared/interfaces/Generic';
import withErrorBoundary from './withErrorBoundary';
import ScreenshotSupport from './ScreenshotSupport';
import MaximizeModal from './MaximizeModal';
import { formatPriceCurrency, getCurrency, showWarning } from 'utils/utils';
import Skeleton from 'react-loading-skeleton';
import { convertCurrencytoShekels, createNewPaymentBlock } from 'services/payment';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';

interface MultiPaymentModalProps {
  onSubmit: (paymentInfos: Credit2000PaymentInfo[]) => void;
  onClose: () => void;
  cardsCount: number;
  total: {
    value: number;
    currency: string;
  };
}

const MultiPaymentModal: FC<MultiPaymentModalProps> = ({
  onSubmit,
  onClose,
  cardsCount,
  total,
}) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [prices, setPrices] = useState<{ value: string; list?: Credit2000PaymentInfo }[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [totalShekels, setTotalShekels] = useState(0);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);

  useEffect(() => {
    (async () => {
      let newprices: any = [];
      for (let i = 0; i < cardsCount; i++) {
        newprices.push({ value: '' });
      }
      const resShekels = await convertCurrencytoShekels(Number(total.value), total.currency);
      setTotalShekels(resShekels.priceinShekels || 0);

      if (cardsCount === 1) {
        const resPayments = await createNewPaymentBlock(resShekels.priceinShekels || 0);
        newprices[0].value = resShekels.priceinShekels.toString();
        newprices[0].list = resPayments;
      }
      setPrices(newprices);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSubmit = () => {
    setIsSubmitted(true);
    const areAllPricesValid = prices.every((p) => p.list);
    if (!areAllPricesValid) {
      showWarning(t('hotel.please_apply_every_price'));
      return;
    }

    const result = prices.map((p) => p.list).filter((p) => p) as Credit2000PaymentInfo[];
    onSubmit(result);
    onClose();
  };

  const apply = async (index: number) => {
    const val = +prices[index].value;
    if (!val || val > totalShekels || val < 0) {
      showWarning(t('hotel.please_enter_valid_price'));
      return;
    }
    const data = await createNewPaymentBlock(val);
    if (data) {
      setPrices(prices.map((p, i) => (index === i ? { ...p, list: data, paymentsCount: '1' } : p)));
    }
  };

  const onWritePrice = (index: number, val: string) => {
    let newPrices = [...prices];
    newPrices[index] = { ...newPrices[index], value: val };
    const totalPriceWithoutLast = newPrices
      .slice(0, -1)
      .reduce((acc, p) => acc + Number(p.value || '0'), 0);
    if (totalPriceWithoutLast > totalShekels) {
      showWarning(t('hotel.please_enter_valid_price'));
      return;
    }

    newPrices[index] = { ...newPrices[index], list: undefined };

    const areAllPricesWithoutLastValid = newPrices
      .slice(0, -1)
      .every((p) => Number(p.value || '0') > 0);
    if (areAllPricesWithoutLastValid) {
      newPrices[newPrices.length - 1] = {
        ...newPrices[newPrices.length - 1],
        value: (totalShekels - totalPriceWithoutLast).toString(),
        list: undefined,
      };
    }
    setPrices(newPrices);
  };

  return createPortal(
    <div className="modal-overlay modal-overlay-top modal-maximizable" ref={modalOverlayRef}>
      <div ref={modalRef} className="modal modal-sm active" style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">{t('passenger.methods_of_payment')}</h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={onClose} />
          </div>
        </div>
        <div className="modal-content">
          <div className="text-md mb-20">
            <span className="text-bold">
              {t('total')}: {formatPriceCurrency(totalShekels, getCurrency('ILS'))}
            </span>
            <span className="text-light ph-5">
              ({formatPriceCurrency(total.value, getCurrency(total.currency))})
            </span>
          </div>
          <div className="pt-5 overflow-hidden pr-2">
            {prices.map((pr, i) => (
              <div className="row mb-20">
                <FormGroup
                  value={pr.value}
                  onChange={(val) => onWritePrice(i, val)}
                  allowOnlyNumbers
                  label={t('price')}
                  movablePlaceholderSm={true}
                  movablePlaceholder={t('price')}
                  className="col-md-3 col-12 mb-10"
                  disabled={i === prices.length - 1}
                  validation={{ required: true }}
                  isSubmitted={isSubmitted}
                />
                <div className="col-md-2 col-12 mb-10">
                  <Button
                    title={t('apply_2')}
                    className="btn btn-primary mr-a w100p"
                    onClick={() => apply(i)}
                  />
                </div>
                {pr.list?.listOfRegularPayments && (
                  <div className="col-md-7 col-12">
                    <SelectCustom
                      options={pr.list.listOfRegularPayments.map((p) => ({
                        value: +p.value,
                        label: p.text,
                      }))}
                      value={pr.list.numberOfRegularPayments}
                      appendToBody={true}
                      onChange={(val) =>
                        setPrices(
                          prices.map((p, index) =>
                            index === i
                              ? { ...p, list: { ...p.list!, numberOfRegularPayments: val } }
                              : p
                          )
                        )
                      }
                    />
                  </div>
                )}

                {i < prices.length - 1 && (
                  <div className="col-12">
                    <div className="divider mt-20"></div>
                  </div>
                )}
              </div>
            ))}
            {!prices.length && (
              <div>
                {Array(cardsCount)
                  .fill(0)
                  .map((_, i) => (
                    <div className="row mb-20">
                      <div className="col-md-5 col-12">
                        <Skeleton height={38} width={'100%'} />
                      </div>
                      <div className="col-md-2">
                        <Skeleton height={38} width={'100%'} />
                      </div>
                      {i < cardsCount - 1 && (
                        <div className="col-12">
                          <div className="divider mt-20"></div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button title={t('hotel.book')} className="btn btn-primary mr-a" onClick={handleSubmit} />
          <Button title={t('close')} className="btn btn-default" onClick={onClose} />
        </div>
      </div>
    </div>
    , document.body);
};

export default withErrorBoundary(MultiPaymentModal, 'MultiPaymentModal');
